import { reconcileElements } from "@excalidraw/excalidraw";
import { MIME_TYPES } from "@excalidraw/common";
import { decompressData } from "@excalidraw/excalidraw/data/encode";
import {
  encryptData,
  decryptData,
} from "@excalidraw/excalidraw/data/encryption";
import { restoreElements } from "@excalidraw/excalidraw/data/restore";
import { getSceneVersion } from "@excalidraw/element";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type {
  ExcalidrawElement,
  FileId,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";

import { getSyncableElements } from ".";

import type { SyncableExcalidrawElement } from ".";
import type Portal from "../collab/Portal";
import type { Socket } from "socket.io-client";

const API_BASE_URL = (() => {
  const explicit = import.meta.env.VITE_APP_API_BASE_URL;
  if (explicit) {
    return explicit.replace(/\/$/, "");
  }
  const wsServer = import.meta.env.VITE_APP_WS_SERVER_URL;
  if (wsServer) {
    return wsServer.replace(/\/$/, "");
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "http://localhost:9083";
})();

/**
 * Extract room/share id from the storage prefix.
 * Prefix format: "/files/rooms/{roomId}" or "/files/shareLinks/{id}"
 */
const extractIdFromPrefix = (prefix: string): string => {
  const parts = prefix.replace(/^\//, "").split("/");
  return parts[parts.length - 1] || "";
};

type StoredScene = {
  sceneVersion: number;
  iv: string;
  ciphertext: string;
};

class SceneVersionCache {
  private static cache = new WeakMap<Socket, number>();
  static get = (socket: Socket) => {
    return SceneVersionCache.cache.get(socket);
  };
  static set = (
    socket: Socket,
    elements: readonly SyncableExcalidrawElement[],
  ) => {
    SceneVersionCache.cache.set(socket, getSceneVersion(elements));
  };
}

export const isSavedToFirebase = (
  portal: Portal,
  elements: readonly ExcalidrawElement[],
): boolean => {
  if (portal.socket && portal.roomId && portal.roomKey) {
    const sceneVersion = getSceneVersion(elements);
    return SceneVersionCache.get(portal.socket) === sceneVersion;
  }
  return true;
};

export const saveFilesToFirebase = async ({
  prefix,
  files,
}: {
  prefix: string;
  files: { id: FileId; buffer: Uint8Array }[];
}) => {
  const roomId = extractIdFromPrefix(prefix);
  const savedFiles: FileId[] = [];
  const erroredFiles: FileId[] = [];

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      try {
        const formData = new FormData();
        const blob = new Blob([buffer], { type: "application/octet-stream" });
        formData.append("file", blob);

        const url = `${API_BASE_URL}/api/scenes/files/${roomId}/${id}`;
        console.info(`[collab-file] UPLOAD -> ${url} (${buffer.byteLength} bytes)`);
        const response = await fetch(url, {
          method: "POST",
          body: formData,
        });

        if (response.ok) {
          console.info(`[collab-file] UPLOAD OK <- ${id}`);
          savedFiles.push(id);
        } else {
          const body = await response.text().catch(() => "");
          console.warn(
            `[collab-file] UPLOAD FAIL ${id}: ${response.status} ${response.statusText}`,
            body,
          );
          erroredFiles.push(id);
        }
      } catch (error: any) {
        console.error(`[collab-file] UPLOAD ERROR ${id}:`, error);
        erroredFiles.push(id);
      }
    }),
  );

  return { savedFiles, erroredFiles };
};

const uint8ArrayToBase64 = (arr: Uint8Array): string => {
  return btoa(String.fromCharCode(...arr));
};

const base64ToUint8Array = (str: string): Uint8Array => {
  const binaryString = atob(str);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

const encryptElements = async (
  key: string,
  elements: readonly ExcalidrawElement[],
): Promise<{ ciphertext: string; iv: string }> => {
  const json = JSON.stringify(elements);
  const encoded = new TextEncoder().encode(json);
  const { encryptedBuffer, iv } = await encryptData(key, encoded);

  return {
    ciphertext: uint8ArrayToBase64(new Uint8Array(encryptedBuffer)),
    iv: uint8ArrayToBase64(new Uint8Array(iv)),
  };
};

const decryptElements = async (
  data: StoredScene,
  roomKey: string,
): Promise<readonly ExcalidrawElement[]> => {
  const ciphertext = base64ToUint8Array(data.ciphertext);
  const iv = base64ToUint8Array(data.iv);

  const decrypted = await decryptData(iv, ciphertext, roomKey);
  const decodedData = new TextDecoder("utf-8").decode(
    new Uint8Array(decrypted),
  );
  return JSON.parse(decodedData);
};

export const saveToFirebase = async (
  portal: Portal,
  elements: readonly SyncableExcalidrawElement[],
  appState: AppState,
) => {
  const { roomId, roomKey, socket } = portal;
  if (!roomId || !roomKey || !socket || isSavedToFirebase(portal, elements)) {
    return null;
  }

  try {
    const sceneVersion = getSceneVersion(elements);
    const { ciphertext, iv } = await encryptElements(roomKey, elements);

    const response = await fetch(`${API_BASE_URL}/api/scenes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ roomId, sceneVersion, iv, ciphertext }),
    });

    if (response.ok) {
      SceneVersionCache.set(socket, elements);
      return elements;
    }
  } catch (error) {
    console.warn("Failed to save scene to local storage", error);
  }

  return null;
};

export const loadFromFirebase = async (
  roomId: string,
  roomKey: string,
  socket: Socket | null,
): Promise<readonly SyncableExcalidrawElement[] | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/scenes/${roomId}`);

    if (response.ok) {
      const storedScene = await response.json() as StoredScene;
      const elements = getSyncableElements(
        restoreElements(await decryptElements(storedScene, roomKey), null, {
          deleteInvisibleElements: true,
        }),
      );

      if (socket) {
        SceneVersionCache.set(socket, elements);
      }

      return elements;
    }
  } catch (error) {
    console.warn("Failed to load scene from local storage", error);
  }

  return null;
};

export const loadFilesFromFirebase = async (
  prefix: string,
  decryptionKey: string,
  filesIds: readonly FileId[],
) => {
  const roomId = extractIdFromPrefix(prefix);
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  await Promise.all(
    [...new Set(filesIds)].map(async (id) => {
      const url = `${API_BASE_URL}/api/scenes/files/${roomId}/${id}`;
      try {
        console.info(`[collab-file] DOWNLOAD -> ${url}`);
        const response = await fetch(url);

        if (response.status < 400) {
          const arrayBuffer = await response.arrayBuffer();
          console.info(
            `[collab-file] DOWNLOAD OK <- ${id} (${arrayBuffer.byteLength} bytes)`,
          );

          const { data, metadata } = await decompressData<BinaryFileMetadata>(
            new Uint8Array(arrayBuffer),
            {
              decryptionKey,
            },
          );

          const dataURL = new TextDecoder().decode(data) as DataURL;

          loadedFiles.push({
            mimeType: metadata.mimeType || MIME_TYPES.binary,
            id,
            dataURL,
            created: metadata?.created || Date.now(),
            lastRetrieved: metadata?.created || Date.now(),
          });
        } else {
          console.warn(
            `[collab-file] DOWNLOAD FAIL ${id}: ${response.status} ${response.statusText}`,
          );
          erroredFiles.set(id, true);
        }
      } catch (error: any) {
        console.error(`[collab-file] DOWNLOAD ERROR ${id}:`, error);
        erroredFiles.set(id, true);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};

export const loadFirebaseStorage = async () => {
  return null;
};

export const getElementsStorageSize = (): number => {
  return 0;
};

export const getTotalStorageSize = (): number => {
  return 0;
};

export const importUsernameFromLocalStorage = (): string | null => {
  return null;
};

export const saveUsernameToLocalStorage = (username: string): void => {
};

export const importFromLocalStorage = (): Promise<{
  elements: readonly SyncableExcalidrawElement[];
  appState: Partial<AppState>;
} | null> => {
  return Promise.resolve(null);
};